// The CRM contact policy, from docs/crm-product-invariants.md and the office/agent model.
//
// - The assigned agent (owner) reads and writes the contact.
// - An office grant on the contact's office reads it and may reassign it or move it; a move
//   needs a grant on BOTH the source and the destination office.
// - A TenantAdmin holds every office of their tenant implicitly.
// - A PlatformAdmin has NO general override. The one break-glass is an office-less contact,
//   and only under an explicit `tenant#` grant (a recorded support session): they may read
//   and write it so it can be given an office. Nothing else in the tenant is reachable.
// - `officeCode` and `assignedAgentCode` are privileged fields: writing them is the move /
//   reassign action, not an ordinary write (see `fields`).
//
// Invariant 4 (no agent tracking) is not a rule here: it forbids surfaces, not reads. The
// `list` scope is the caller's own contacts and the offices they hold, never another agent's.
import { allOf, anyOf, attrIsNull, hasOfficeGrantOn, hasTenantGrant, isMember, isOwner, isRole, platformAdmin, } from '../policy/rules.js';
import { ORDINARY_FIELDS } from '../policy/types.js';
const tenantAdmin = isRole('TenantAdmin');
const owner = isOwner('assignedAgentCode');
const sourceOffice = hasOfficeGrantOn('officeCode');
const destinationOffice = hasOfficeGrantOn('targetOfficeCode');
/** Platform break-glass: a support session on the tenant, and only for an office-less contact. */
const breakGlass = allOf(platformAdmin, hasTenantGrant, attrIsNull('officeCode'));
const read = anyOf(tenantAdmin, owner, sourceOffice, breakGlass);
const write = anyOf(tenantAdmin, owner, breakGlass);
const reassign = anyOf(tenantAdmin, sourceOffice, breakGlass);
const moveOffice = anyOf(tenantAdmin, allOf(sourceOffice, destinationOffice), breakGlass);
export const contactPolicy = {
    kind: 'contact',
    scope: 'tenant',
    actions: {
        list: read,
        read,
        /** Create into your own book, into an office you hold, or (admin) anywhere. */
        create: anyOf(tenantAdmin, allOf(isMember, anyOf(owner, sourceOffice)), breakGlass),
        write,
        delete: anyOf(tenantAdmin, owner),
        reassign,
        'move-office': moveOffice,
    },
    writeAction: 'write',
    fields: {
        [ORDINARY_FIELDS]: write,
        officeCode: moveOffice,
        assignedAgentCode: reassign,
    },
};
//# sourceMappingURL=contact.js.map